import React from "react";
import {
  FlightPassengerSelectors,
  FlightSelectors,
  GordianSeatSegment,
  SeatSelection,
  SeatSelectors,
  useCheckoutStateSelector,
  GordianSeatWithRewards,
  useCheckoutState,
  SeatEventTypes,
} from "@capone/checkout";
import { RouteComponentProps } from "react-router-dom";
import { ActionButton } from "halifax";
import { Typography } from "@material-ui/core";
import { SeatMapResponseEnum, SKIPPED_SEAT_SELECTION } from "redmond";
import { personToIPerson } from "../../../../utils/personToIPerson";
import { PackagesSeatSelectionConnectorProps } from "./container";
import { convertUsdToAllRewards } from "../../../../api/v0/rewards/convertUsdToAllRewards";
import { TEvent } from "../../state/events";
import { PackagesMachineContext } from "../../state/types";
import { PATH_HOME } from "../../../../utils/paths";
import {
  CONTINUE_CTA_TEXT,
  CONTINUE_CTA_TEXT_V1,
  SEAT_SELECTION_SUBTITLE,
  SEAT_SELECTION_TITLE_V1,
  SEAT_SELECTION_VALIDATION_MESSAGE,
} from "./textConstants";
import { PackagesPriceBreakdown } from "../PriceBreakdown";
import { DesktopPackageBookValidationError } from "../DesktopPackagesBookWorkflow";
import { MobileBookingSteps } from "../MobileBookingSteps";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import styles from "./styles.module.scss";
import { trackEvent } from "../../../../api/v0/trackEvent";
import { useIsTravelApp } from "@capone/common";

export interface IPackagesSeatSelectionProps
  extends RouteComponentProps,
    PackagesSeatSelectionConnectorProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;
}

export const PackagesSeatSelection = ({
  selectedRewardsAccountId,
  history,
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
}: IPackagesSeatSelectionProps) => {
  const expState = useExperiments();

  const { isTravelApp } = useIsTravelApp();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const tripDetails = useCheckoutStateSelector(
    FlightSelectors.getSelectedTripDetailsParent
  );
  const airportMap = useCheckoutStateSelector(FlightSelectors.getAirportMap);
  const passengers = useCheckoutStateSelector(
    FlightPassengerSelectors.getAllSelectedUserPassengersParent
  );

  const isLoading = useCheckoutStateSelector(
    SeatSelectors.getIsSeatSelectionLoading
  );
  const selectedSeats = useCheckoutStateSelector(
    SeatSelectors.getSelectedSeats
  );
  const cheapestSeat = useCheckoutStateSelector(SeatSelectors.getCheapestSeat);
  const seatMapHtml = useCheckoutStateSelector(SeatSelectors.getSeatMapHtml);
  const totalSeatPricing = useCheckoutStateSelector(
    SeatSelectors.getSeatTotalPricing
  );
  const skippedSeatSelection = useCheckoutStateSelector(
    SeatSelectors.getSkipSeatSelection
  );
  const seatMapAvailability = useCheckoutStateSelector(
    SeatSelectors.getSeatMapAvailability
  );
  const seatSlices = useCheckoutStateSelector(SeatSelectors.getSeatSlices);
  const seatSelectionErrorModalOpen = useCheckoutStateSelector(
    SeatSelectors.getSeatSelectionErrorOpen
  );
  const seatSelectionError = useCheckoutStateSelector(
    SeatSelectors.getSeatSelectionError
  );

  const handleSetSelectedSeats = (
    seats: GordianSeatWithRewards[],
    seatSegments: GordianSeatSegment[]
  ) => send({ type: SeatEventTypes.SELECT_SEATS, seats, seatSegments });

  const handleSkipSeatSelection = () => {
    send({ type: SeatEventTypes.SKIP_SEAT_SELECTION });
  };

  const searchAgain = () => {
    history.push(PATH_HOME);
  };

  const canContinue =
    isMobile || !!selectedSeats.length || skippedSeatSelection;

  const handleContinue = () => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send({ type: SeatEventTypes.NEXT });
      if (skippedSeatSelection) {
        trackEvent({
          eventName: SKIPPED_SEAT_SELECTION,
          properties: {},
          encryptedProperties: [],
        });
      }
    } else {
      setValidationErrorTypes?.(["seats"]);
    }
  };

  const handleBack = () => {
    send({ type: SeatEventTypes.PREVIOUS });
  };

  React.useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  return (
    <>
      <SeatSelection
        className={styles["packages-seat-selection"]}
        tripDetails={tripDetails}
        airports={airportMap || {}}
        passengers={passengers.map(personToIPerson)}
        seatSelectionLoading={isLoading}
        selectedSeats={selectedSeats}
        cheapestSeat={cheapestSeat}
        seatMapHtml={seatMapHtml}
        totalSeatPricing={totalSeatPricing}
        skippedSeatSelection={skippedSeatSelection}
        seatMapAvailability={seatMapAvailability}
        selectedRewardsAccountId={selectedRewardsAccountId || undefined}
        seatSlices={seatSlices || []}
        seatSelectionErrorModalOpen={seatSelectionErrorModalOpen}
        seatSelectionError={seatSelectionError}
        setSelectedSeats={handleSetSelectedSeats}
        handleSearchAgain={searchAgain}
        handleSkipSeatSelection={handleSkipSeatSelection}
        convertUsdToAllRewards={convertUsdToAllRewards}
        isMobile={isMobile}
        mobileHeaderElement={
          isPackageCheckoutOptimizationExperiment ? (
            <Typography className={styles["mobile-header-text"]}>
              {SEAT_SELECTION_TITLE_V1}
            </Typography>
          ) : (
            <PackagesPriceBreakdown isMobile dropdown />
          )
        }
        onContinue={handleContinue}
        onBack={handleBack}
        mobileTopContent={
          isPackageCheckoutOptimizationExperiment ? (
            <div className={styles["mobile-custom-header"]}>
              <MobileBookingSteps currentStep={4} />
              <hr className={styles.separator} />
              {seatMapAvailability === SeatMapResponseEnum.SeatMapAvailable ? (
                <Typography className={styles.subtitle}>
                  {SEAT_SELECTION_SUBTITLE}
                </Typography>
              ) : undefined}
            </div>
          ) : undefined
        }
        mobileBottomContent={
          isPackageCheckoutOptimizationExperiment ? (
            <PackagesPriceBreakdown isMobile popover />
          ) : undefined
        }
        mobileContinueText={
          isPackageCheckoutOptimizationExperiment
            ? CONTINUE_CTA_TEXT_V1
            : undefined
        }
        hideBackButton={isTravelApp}
        isXmodRedesign={isXmodRedesign}
      />
      {validationErrorTypes?.includes("seats") && (
        <Typography className="seat-selection-validation-message">
          {SEAT_SELECTION_VALIDATION_MESSAGE}
        </Typography>
      )}
      {!isMobile && (
        <ActionButton
          onClick={handleContinue}
          message={CONTINUE_CTA_TEXT}
          className="packages-continue-button seats"
        />
      )}
    </>
  );
};
